import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import {
  SupportPriority,
  SupportStatus,
  SupportTicket
} from 'src/app/core/models/support';
import { SupportService } from 'src/app/core/services/support.service';

@Component({
  selector: 'app-support',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule],
  templateUrl: './support.component.html',
  styleUrl: './support.component.scss'
})
export class SupportComponent implements OnInit {

  supportForm!: FormGroup;

  isSubmitting = false;
  isWhatsAppLoading = false;
  isTicketsLoading = false;

  submitSuccessMessage = '';
  submitErrorMessage = '';

  currentUserName = '';
  currentUserEmail = '';
  currentUserPhone = '';

  recentTickets: SupportTicket[] = [];

  page = 1;
  limit = 5;

  priorities: SupportPriority[] = ['LOW', 'MEDIUM', 'HIGH', 'URGENT'];
  statuses: SupportStatus[] = ['OPEN', 'IN_PROGRESS', 'RESOLVED'];

  modules: string[] = [
    'Dashboard',
    'Patients Module',
    'Orders Module',
    'Protocols Module',
    'Chat Module',
    'Call Module',
    'Calendar Module',
    'Notifications Module',
    'Reports Module',
    'Billing Module',
    'Settings Module',
    'Other'
  ];

  selectedTicket: SupportTicket | null = null;
  noteText = '';
  isTicketModalOpen = false;
  isUpdatingTicket = false;

  selectedAttachmentFile: File | null = null;
  selectedAttachmentName = '';
  attachmentError = '';

  constructor(
    private fb: FormBuilder,
    private supportService: SupportService
  ) { }

  ngOnInit(): void {
    this.initForm();
    this.loadCurrentUserFromStorage();
    this.patchCurrentUserToForm();
    this.loadRecentTickets();
  }

  initForm(): void {
    this.supportForm = this.fb.group({
      clientName: [''],
      clientEmail: ['', [Validators.email]],
      clientPhone: [''],
      moduleName: ['', Validators.required],
      issueTitle: ['', [Validators.required, Validators.maxLength(120)]],
      errorMessage: [''],
      description: ['', [Validators.required, Validators.maxLength(2000)]],
      expectedSolution: [''],
      priority: ['MEDIUM', Validators.required]
    });
  }

  loadCurrentUserFromStorage(): void {
    const storedUser = JSON.parse(localStorage.getItem('authenticatedUser') || 'null');

    this.currentUserName = storedUser?.fullName || storedUser?.name || '';
    this.currentUserEmail = storedUser?.email || '';
    this.currentUserPhone = storedUser?.phoneNo || '';
  }

  patchCurrentUserToForm(): void {
    this.supportForm.patchValue({
      clientName: this.currentUserName,
      clientEmail: this.currentUserEmail,
      clientPhone: this.currentUserPhone
    });
  }

  onAttachmentSelected(event: Event): void {
    this.attachmentError = '';

    const input = event.target as HTMLInputElement;
    const file = input.files && input.files.length ? input.files[0] : null;

    if (!file) {
      this.selectedAttachmentFile = null;
      this.selectedAttachmentName = '';
      return;
    }

    const allowedMimeTypes = [
      'image/jpeg',
      'image/jpg',
      'image/png',
      'image/webp',
      'image/gif',
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'text/plain'
    ];

    const maxSize = 10 * 1024 * 1024; // 10MB

    if (!allowedMimeTypes.includes(file.type)) {
      this.attachmentError = 'Invalid file type. Only images, pdf, doc, docx, xls, xlsx and txt are allowed.';
      this.selectedAttachmentFile = null;
      this.selectedAttachmentName = '';

      input.value = '';
      return;
    }

    if (file.size > maxSize) {
      this.attachmentError = 'File size must be less than 10MB.';
      this.selectedAttachmentFile = null;
      this.selectedAttachmentName = '';

      input.value = '';
      return;
    }

    this.selectedAttachmentFile = file;
    this.selectedAttachmentName = file.name;
  }

  loadRecentTickets(): void {
    this.isTicketsLoading = true;

    this.supportService.listTickets({
      page: this.page,
      limit: this.limit
    }).subscribe({
      next: (res) => {
        this.recentTickets = res?.data?.tickets || [];
        this.isTicketsLoading = false;
      },
      error: (err) => {
        console.error('Support tickets fetch failed:', err);
        this.recentTickets = [];
        this.isTicketsLoading = false;
      }
    });
  }

  submitTicket(): void {
    this.submitSuccessMessage = '';
    this.submitErrorMessage = '';
    this.attachmentError = '';

    if (this.supportForm.invalid) {
      this.supportForm.markAllAsTouched();
      this.submitErrorMessage = 'Please fill all required fields correctly.';
      return;
    }

    const formData = new FormData();
    formData.append('clientName', this.supportForm.value.clientName || '');
    formData.append('clientEmail', this.supportForm.value.clientEmail || '');
    formData.append('clientPhone', this.supportForm.value.clientPhone || '');
    formData.append('moduleName', this.supportForm.value.moduleName || '');
    formData.append('issueTitle', this.supportForm.value.issueTitle || '');
    formData.append('errorMessage', this.supportForm.value.errorMessage || '');
    formData.append('description', this.supportForm.value.description || '');
    formData.append('expectedSolution', this.supportForm.value.expectedSolution || '');
    formData.append('priority', this.supportForm.value.priority || 'MEDIUM');

    if (this.selectedAttachmentFile) {
      formData.append('attachment', this.selectedAttachmentFile);
    }

    this.isSubmitting = true;

    this.supportService.createTicket(formData).subscribe({
      next: (res) => {
        this.isSubmitting = false;

        const ticketNo = res?.data?.ticket?.ticketNo || '';
        const emailSent = res?.data?.emailSent;

        this.submitSuccessMessage = emailSent
          ? `Support ticket ${ticketNo} submitted successfully. Our support team has also been notified by email.`
          : `Support ticket ${ticketNo} submitted successfully.`;

        this.supportForm.reset({
          clientName: this.currentUserName,
          clientEmail: this.currentUserEmail,
          clientPhone: this.currentUserPhone,
          moduleName: '',
          issueTitle: '',
          errorMessage: '',
          description: '',
          expectedSolution: '',
          priority: 'MEDIUM'
        });

        this.selectedAttachmentFile = null;
        this.selectedAttachmentName = '';
        this.attachmentError = '';

        this.supportForm.markAsPristine();
        this.supportForm.markAsUntouched();

        this.loadRecentTickets();
      },
      error: (err) => {
        console.error('Create support ticket failed:', err);
        this.isSubmitting = false;
        this.submitErrorMessage =
          err?.error?.message ||
          err?.message ||
          'Failed to submit support ticket. Please try again.';
      }
    });
  }

  openWhatsAppSupport(): void {
    this.submitSuccessMessage = '';
    this.submitErrorMessage = '';

    const payload = {
      clientName: this.supportForm.value.clientName || this.currentUserName || '',
      clientEmail: this.supportForm.value.clientEmail || this.currentUserEmail || '',
      clientPhone: this.supportForm.value.clientPhone || this.currentUserPhone || '',
      moduleName: this.supportForm.value.moduleName || '',
      issueTitle: this.supportForm.value.issueTitle || '',
      errorMessage: this.supportForm.value.errorMessage || '',
      description: this.supportForm.value.description || '',
      expectedSolution: this.supportForm.value.expectedSolution || '',
      priority: this.supportForm.value.priority || 'MEDIUM'
    };

    this.isWhatsAppLoading = true;

    this.supportService.generateWhatsAppLink(payload).subscribe({
      next: (res) => {
        this.isWhatsAppLoading = false;
        const link = res?.data?.whatsappLink;
        if (link) {
          window.open(link, '_blank');
        } else {
          this.submitErrorMessage = 'Unable to generate WhatsApp support link.';
        }
      },
      error: (err) => {
        console.error('WhatsApp link generation failed:', err);
        this.isWhatsAppLoading = false;
        this.submitErrorMessage =
          err?.error?.message ||
          err?.message ||
          'Unable to open WhatsApp support right now.';
      }
    });
  }

  viewTicket(ticket: SupportTicket): void {
    this.selectedTicket = ticket;
    this.noteText = '';
    this.isTicketModalOpen = true;
  }

  closeTicketModal(): void {
    this.isTicketModalOpen = false;
    this.selectedTicket = null;
    this.noteText = '';
  }

  updateStatus(status: SupportStatus): void {
    if (!this.selectedTicket?._id) return;

    this.isUpdatingTicket = true;

    this.supportService.updateTicketStatus(this.selectedTicket._id, { status }).subscribe({
      next: (res) => {
        this.selectedTicket = res?.data || this.selectedTicket;
        this.isUpdatingTicket = false;
        this.loadRecentTickets();
      },
      error: (err) => {
        console.error('Ticket status update failed:', err);
        this.isUpdatingTicket = false;
      }
    });
  }

  addNote(): void {
    if (!this.selectedTicket?._id || !this.noteText.trim()) return;

    this.isUpdatingTicket = true;

    this.supportService.addTicketNote(this.selectedTicket._id, {
      message: this.noteText.trim()
    }).subscribe({
      next: (res) => {
        this.selectedTicket = res?.data || this.selectedTicket;
        this.noteText = '';
        this.isUpdatingTicket = false;
        this.loadRecentTickets();
      },
      error: (err) => {
        console.error('Add note failed:', err);
        this.isUpdatingTicket = false;
      }
    });
  }

  deleteTicket(id: string): void {
    const ok = window.confirm('Are you sure you want to delete this support ticket?');
    if (!ok) return;

    this.supportService.deleteTicket(id).subscribe({
      next: () => {
        if (this.selectedTicket?._id === id) {
          this.closeTicketModal();
        }
        this.loadRecentTickets();
      },
      error: (err) => {
        console.error('Delete ticket failed:', err);
      }
    });
  }

  getPriorityClass(priority: string): string {
    switch (priority) {
      case 'LOW':
        return 'low';
      case 'MEDIUM':
        return 'medium';
      case 'HIGH':
        return 'high';
      case 'URGENT':
        return 'urgent';
      default:
        return 'medium';
    }
  }

  getStatusClass(status: string): string {
    switch (status) {
      case 'OPEN':
        return 'open';
      case 'IN_PROGRESS':
        return 'progress';
      case 'RESOLVED':
        return 'resolved';
      default:
        return 'open';
    }
  }

  getStatusIcon(status: string): string {
    switch (status) {
      case 'OPEN':
        return 'bi bi-exclamation-circle';
      case 'IN_PROGRESS':
        return 'bi bi-hourglass-split';
      case 'RESOLVED':
        return 'bi bi-check-circle';
      default:
        return 'bi bi-circle';
    }
  }

  formatDate(date: string | null | undefined): string {
    if (!date) return '-';

    const d = new Date(date);
    if (isNaN(d.getTime())) return '-';

    return d.toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  hasError(controlName: string, errorName?: string): boolean {
    const control = this.supportForm.get(controlName);
    if (!control) return false;
    if (errorName) {
      return !!(control.touched && control.hasError(errorName));
    }
    return !!(control.touched && control.invalid);
  }

  get initials(): string {
    const name = this.currentUserName?.trim() || 'BW';
    const parts = name.split(' ').filter(Boolean);

    if (parts.length === 1) {
      return parts[0].slice(0, 2).toUpperCase();
    }

    return `${parts[0][0] || ''}${parts[1][0] || ''}`.toUpperCase();
  }
}